import { IResponseData } from "@/utilities/types/requests.ts";
import { IFloor } from "@/utilities/types/Floor.ts";
import FloorPlansRepository from "@/utilities/repositories/FloorPlans.ts";
import useApi from "@/hooks/useApi.tsx";
import { useTableServer } from "@/hooks/useTableServer.tsx";
import { useCallback, useMemo, useState } from "react";
import ControlledTable from "@/components/controlled-table";
import { useColumn } from "@/hooks/use-column.ts";
import { GetColumns } from "@/app/app-shared/floor-plan/table/columns.tsx";
import { Roles } from "@/utilities/types/Users.ts";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import ModalCreateEditFloor from "@/app/app-shared/floor-plan/create-edit/modal-edit.tsx";
import { Button, Title } from "rizzui";
import { BulkMarkAsSoldModal } from "@/app/app-shared/units/bulk-mark-as-sold";

type AvailabilityFilter = "all" | "sold" | "available";

const FloorPlanTable = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const [isBulkMarkAsSoldOpen, setIsBulkMarkAsSoldOpen] =
    useState<boolean>(false);
  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const { request: createFloor } = useApi<IResponseData<IFloor>>({
    request: FloorPlansRepository.createFloor,
    enableToast: true,
  });

  const { request: deleteFloorItem } = useApi<IResponseData<IFloor>>({
    request: FloorPlansRepository.RemoveFloor,
    enableToast: true,
  });

  const {
    handleReset,
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handleSort,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    refresh,
    sortConfig,
    filters,
    updateFilter,
  } = useTableServer({
    server: {
      request: FloorPlansRepository.getFloor,
    },
    initialFilterState: {
      projectId,
    },
    initialSortConfig: {
      key: "area",
      direction: "asc",
    },
    pagination: {
      limit: 30,
      page: 1,
    },
  });

  const handleAvailabilityFilter = (filter: AvailabilityFilter) => {
    updateFilter("availability", filter === "all" ? undefined : filter);
  };

  const handleResetView = () => {
    handleReset();
  };

  const availabilityFilter = filters?.availability || "all";

  const onAddFloor = useCallback(async () => {
    await createFloor()
      .then(() => {
        refresh(); // Recall API
      })
      .catch((err: any) => {
        toast.error(err?.message || "Something went wrong");
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const onDeleteItem = useCallback(
    async (id: string) => {
      await deleteFloorItem(id);
      refresh(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [refresh],
  );

  const columns = useMemo(
    () =>
      GetColumns({
        data: tableData,
        onDeleteItem,
        onHeaderCellClick,
        projectId: projectId ?? "",
        reset: handleReset,
        role: Roles.ADMIN,
        refresh,
        sortConfig,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onHeaderCellClick, onDeleteItem, onAddFloor, tableData],
  );
  const { visibleColumns } = useColumn(columns);
  return (
    <div className={"col-span-full mt-4"}>
      <div className=" w-full flex items-center justify-between">
        <Title as="h5">Floorplan</Title>
        {projectId && (
          <div className="flex gap-2 items-start">
            <ModalCreateEditFloor
              reset={handleReset}
              refresh={refresh}
              projectId={projectId}
            />
            <Button onClick={() => setIsBulkMarkAsSoldOpen(true)}>
              Mark as Sold
            </Button>
          </div>
        )}
      </div>
      <div className="mb-4 flex w-full flex-wrap items-center justify-between gap-3">
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
          <Button
            size="sm"
            variant={availabilityFilter === "sold" ? "solid" : "outline"}
            onClick={() => handleAvailabilityFilter("sold")}
          >
            Fully Sold
          </Button>
          <Button
            size="sm"
            variant={availabilityFilter === "available" ? "solid" : "outline"}
            onClick={() => handleAvailabilityFilter("available")}
          >
            Fully Available
          </Button>
          <Button
            size="sm"
            variant="flat"
            className="text-gray-500"
            onClick={handleResetView}
            disabled={availabilityFilter === "all"}
          >
            Reset View
          </Button>
        </div>
      </div>
      <ControlledTable
        scroll={{ x: true }}
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        columns={visibleColumns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />

      <BulkMarkAsSoldModal
        isOpen={isBulkMarkAsSoldOpen}
        onClose={() => setIsBulkMarkAsSoldOpen(false)}
        projectId={projectId ?? ""}
        onSuccess={refresh}
      />
    </div>
  );
};

export default FloorPlanTable;
